'use client';

import { useMemo } from 'react';

import { LogoItem } from '@app/components/ui/LogoLoop';

// Types
export interface LogoVariant {
  name: string;
  path: string;
  darkPath?: string; // Dark mode version
  description: string;
  sizes: {
    sm: string;
    md: string;
    lg: string;
    xl: string;
  };
}

export interface LogoConfig {
  variants: {
    full: LogoVariant;
    icon: LogoVariant;
    animated: LogoVariant;
  };
  defaultVariant: 'full' | 'icon' | 'animated';
  colors: {
    primary: {
      from: string;
      to: string;
    };
    secondary: {
      from: string;
      to: string;
    };
  };
}

export interface NavigationItem {
  label: string;
  href: string;
  icon?: React.ReactNode;
  active?: boolean;
  external?: boolean;
  children?: NavigationItem[];
}

export interface NavigationConfig {
  main: NavigationItem[];
  footer: {
    label: string;
    menu: NavigationItem[];
  }[];
  subfooter: {
    label: string;
    href: string;
  }[];
}

export interface WebsiteInfo {
  name: string;
  fullName: string;
  tagline: string;
  description: string;
  version: string;
  author: {
    name: string;
    email: string;
    website?: string;
  };
  contact: {
    email: string;
    phone?: string;
    address?: string;
  };
  social: {
    facebook?: string;
    twitter?: string;
    linkedin?: string;
    youtube?: string;
    instagram?: string;
  };
  legal: {
    termsOfService?: string;
    privacyPolicy?: string;
    cookiePolicy?: string;
  };
  seo: {
    keywords: string[];
    canonicalUrl: string;
    ogImage: string;
  };
  partner?: LogoItem[];
}

export interface CommonData {
  logo: LogoConfig;
  website: WebsiteInfo;
  navigation: NavigationConfig;
  theme: {
    colors: {
      primary: string;
      secondary: string;
      accent: string;
      background: string;
      surface: string;
    };
    gradients: {
      primary: string;
      secondary: string;
      accent: string;
    };
  };
  verifyApp: {
    appStoreUrl: string;
    playStoreUrl: string;
  };
  hotestProvinces: {
    provinceCode: number;
    name: string;
    image: string;
    grid: string;
  }[];
}

// Hook implementation
export function useCommonData(): CommonData {
  return useMemo(
    () => ({
      logo: {
        variants: {
          full: {
            name: 'Full Logo',
            path: '/images/logo-full.svg',
            darkPath: '/images/logo-full-dark.svg',
            description: 'Logo đầy đủ với icon và text PROPX',
            sizes: {
              sm: 'h-6',
              md: 'h-8',
              lg: 'h-12',
              xl: 'h-16',
            },
          },
          icon: {
            name: 'Icon Only',
            path: '/images/logo.svg',
            darkPath: '/images/logo-dark.svg',
            description: 'Chỉ icon PropX không có text',
            sizes: {
              sm: 'w-6 h-6',
              md: 'w-8 h-8',
              lg: 'w-12 h-12',
              xl: 'w-16 h-16',
            },
          },
          animated: {
            name: 'Animated Logo',
            path: '/images/logo-animated.svg',
            darkPath: '/images/logo-animated-dark.svg',
            description: 'Logo với hiệu ứng animation cho splash screen',
            sizes: {
              sm: 'w-32 h-32',
              md: 'w-48 h-48',
              lg: 'w-64 h-64',
              xl: 'w-80 h-80',
            },
          },
        },
        defaultVariant: 'full',
        colors: {
          primary: {
            from: '#0765FE',
            to: '#002F89',
          },
          secondary: {
            from: '#2BB0FB',
            to: '#0057FF',
          },
        },
      },

      navigation: {
        main: [
          { label: 'Trang chủ', href: '/', active: true },
          { label: 'Tin bán', href: '/properties' },
          { label: 'Giới thiệu', href: '/about' },
          { label: 'Điều khoản', href: '/term' },
          { label: 'Chính sách', href: '/policy' },
        ],
        footer: [
          {
            label: 'PropX',
            menu: [
              { label: 'Trang chủ', href: '/' },
              { label: 'Tin bán', href: '/listing' },
              { label: 'Giới thiệu', href: '/about' },
              { label: 'Liên hệ', href: '/contact' },
            ],
          },
          {
            label: 'Liên kết nhanh',
            menu: [
              { label: 'Văn phòng đăng ký đất đai', href: '/' },
              { label: 'Văn bản chính sách', href: '/' },
              { label: 'Văn bản hướng dẫn', href: '/' },
              { label: 'Văn bản pháp lý', href: '/' },
            ],
          },
          {
            label: 'Hỗ trợ',
            menu: [
              { label: 'Tải ứng dụng', href: '/' },
              { label: 'Hướng dẫn sử dụng', href: '/' },
              { label: 'Câu hỏi thường gặp', href: '/' },
              { label: 'Liên hệ', href: '/contact' },
            ],
          },
        ],
        subfooter: [
          { label: 'Điều khoản sử dụng', href: '/term' },
          { label: 'Chính sách bảo mật', href: '/policy' },
        ],
      },

      website: {
        name: process.env.NEXT_PUBLIC_APP_NAME || 'PropX',
        fullName: `${process.env.NEXT_PUBLIC_APP_NAME || 'PropX'} - Nền tảng Quản lý Bất động sản`,
        tagline: 'Quản lý Bất động sản Thông minh',
        description:
          process.env.NEXT_PUBLIC_APP_DESCRIPTION ||
          'Nền tảng quản lý bất động sản hiện đại và thông minh, giúp bạn dễ dàng tìm kiếm, quản lý và đầu tư bất động sản một cách hiệu quả.',
        version: process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0',

        author: {
          name: 'PropX Team',
          email: 'team@propx.vn',
          website: process.env.NEXT_PUBLIC_WEBSITE_URL || 'https://propx.vn',
        },

        contact: {
          email: process.env.NEXT_PUBLIC_CONTACT_EMAIL || 'support@propx.vn',
          phone: process.env.NEXT_PUBLIC_CONTACT_PHONE || '+84 982 331 364',
          address:
            process.env.NEXT_PUBLIC_CONTACT_ADDRESS ||
            'Tầng 11, Tháp B, Khách Sạn Thể Thao, 15 Lê Văn Thiêm, Phường Thanh Xuân, Quận Thanh Xuân, Thành phố Hà Nội, Việt Nam',
        },

        social: {
          facebook: process.env.NEXT_PUBLIC_FACEBOOK_URL || 'https://facebook.com/propx.vn',
          twitter: process.env.NEXT_PUBLIC_TWITTER_URL || 'https://twitter.com/propx_vn',
          linkedin: process.env.NEXT_PUBLIC_LINKEDIN_URL || 'https://linkedin.com/company/propx-vn',
          youtube: process.env.NEXT_PUBLIC_YOUTUBE_URL || 'https://youtube.com/c/propx-vn',
          instagram: process.env.NEXT_PUBLIC_INSTAGRAM_URL || 'https://instagram.com/propx.vn',
        },

        legal: {
          termsOfService: '/term',
          privacyPolicy: '/policy',
          cookiePolicy: '/cookies',
        },

        seo: {
          keywords: [
            'bất động sản',
            'quản lý property',
            'đầu tư bds',
            'real estate vietnam',
            'property management',
            'PropX',
            'bds thông minh',
          ],
          canonicalUrl: process.env.NEXT_PUBLIC_CANONICAL_URL || 'https://propx.vn',
          ogImage: '/images/og-image.jpg',
        },
        partner: [
          {
            label: '2ID',
            href: 'https://2id.vn',
            src: '/images/partners/2id.svg',
          },
          {
            label: '9pay',
            href: 'https://9pay.vn',
            src: '/images/partners/9pay.svg',
          },
          {
            label: 'VPBank',
            href: 'https://vpbank.vn',
            src: '/images/partners/vpbank.svg',
          },
        ],
        mobileApp: {
          logo: '/images/logo.svg',
          title: 'Trải nghiệm nền tảng PROPX trên các nền tảng thiết bị di động thông minh',
          description:
            'Đăng tin nhanh chóng, quản lý tin hiệu quả và tiếp cận đúng khách hàng mục tiêu.',
          imageUrl: '/images/propx-app.png',
          stores: {
            appStore: {
              url: 'https://apps.apple.com/xxx',
              text: 'Tải ứng dụng <br>trên <b>App Store</b>',
            },
            playStore: {
              url: 'https://play.google.com/xxx',
              text: 'Tải ứng dụng <br>trên <b>Google Play</b>',
            },
          },
        },
        copyright: '© 2024 Propx. All rights reserved.',
      },

      theme: {
        colors: {
          primary: '#0765FE',
          secondary: '#2BB0FB',
          accent: '#0057FF',
          background: '#FFFFFF',
          surface: '#F8FAFC',
        },
        gradients: {
          primary: 'linear-gradient(135deg, #0765FE 0%, #002F89 100%)',
          secondary: 'linear-gradient(135deg, #2BB0FB 0%, #0057FF 100%)',
          accent: 'linear-gradient(135deg, #0765FE 0%, #2BB0FB 100%)',
        },
      },

      verifyApp: {
        appStoreUrl: 'https://apps.apple.com/xxx',
        playStoreUrl: 'https://play.google.com/xxx',
      },
      hotestProvinces: [
        {
          provinceCode: 1,
          name: 'Thành phố Hà Nội',
          image: '/images/figma/hanoi-property.png',
          grid: 'md:col-span-2 md:row-span-2 col-span-1 row-span-1',
        },
        {
          provinceCode: 79,
          name: 'Thành phố Hồ Chí Minh',
          image: '/images/figma/hcm-property-541ab4.png',
          grid: 'col-span-1 row-span-1',
        },
        {
          provinceCode: 38,
          name: 'Tỉnh Thanh Hóa',
          image: '/images/figma/thanh-hoa-property-541ab4.png',
          grid: 'col-span-1 row-span-1',
        },
        {
          provinceCode: 56,
          name: 'Tỉnh Khánh Hòa',
          image: '/images/figma/khanh-hoa-property.png',
          grid: 'col-span-1 row-span-1',
        },
        {
          provinceCode: 48,
          name: 'Thành phố Đà Nẵng',
          image: '/images/figma/da-nang-property-541ab4.png',
          grid: 'col-span-1 row-span-1',
        },
      ],
    }),
    []
  );
}

// Utility hooks for specific data
export function useLogoConfig(): LogoConfig {
  const { logo } = useCommonData();
  return logo;
}

export function useWebsiteInfo(): WebsiteInfo {
  const { website } = useCommonData();
  return website;
}

export function useThemeConfig() {
  const { theme } = useCommonData();
  return theme;
}

export function useNavigationConfig(): NavigationConfig {
  const { navigation } = useCommonData();
  return navigation;
}

export function useMainNavigation(): NavigationItem[] {
  const { navigation } = useCommonData();
  return navigation.main;
}

export function useFooterNavigation(): { label: string; menu: NavigationItem[] }[] {
  const { navigation } = useCommonData();
  return navigation.footer;
}

// Helper hooks
export function useLogoPath(
  variant: 'full' | 'icon' | 'animated' = 'full',
  theme?: 'light' | 'dark' | 'auto'
): string {
  const { logo } = useCommonData();
  const config = logo.variants[variant];

  if (theme === 'dark') {
    return config.darkPath || config.path;
  } else if (theme === 'light') {
    return config.path;
  } else {
    // Auto mode - check system preference
    if (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-color-scheme: dark)').matches
    ) {
      return config.darkPath || config.path;
    }
    return config.path;
  }
}

export function useLogoSize(
  variant: 'full' | 'icon' | 'animated',
  size: 'sm' | 'md' | 'lg' | 'xl'
): string {
  const { logo } = useCommonData();
  return logo.variants[variant].sizes[size];
}

// Pure helper functions (không sử dụng hooks)
export const LogoHelper = {
  getPath: (
    variant: 'full' | 'icon' | 'animated' = 'full',
    theme?: 'light' | 'dark' | 'auto'
  ): string => {
    const configs = {
      full: {
        path: '/images/logo-full.svg',
        darkPath: '/images/logo-full-dark.svg',
      },
      icon: {
        path: '/images/logo.svg',
        darkPath: '/images/logo-dark.svg',
      },
      animated: {
        path: '/images/logo-animated.svg',
        darkPath: '/images/logo-animated-dark.svg',
      },
    };

    const config = configs[variant];

    if (theme === 'dark') {
      return config.darkPath || config.path;
    } else if (theme === 'light') {
      return config.path;
    } else {
      // Auto mode - check system preference
      if (
        typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-color-scheme: dark)').matches
      ) {
        return config.darkPath || config.path;
      }
      return config.path;
    }
  },

  getSize: (variant: 'full' | 'icon' | 'animated', size: 'sm' | 'md' | 'lg' | 'xl'): string => {
    const sizes = {
      full: { sm: 'h-6', md: 'h-8', lg: 'h-12', xl: 'h-16' },
      icon: { sm: 'w-6 h-6', md: 'w-8 h-8', lg: 'w-12 h-12', xl: 'w-16 h-16' },
      animated: { sm: 'w-32 h-32', md: 'w-48 h-48', lg: 'w-64 h-64', xl: 'w-80 h-80' },
    };
    return sizes[variant][size];
  },

  getColors: () => ({
    primary: { from: '#0765FE', to: '#002F89' },
    secondary: { from: '#2BB0FB', to: '#0057FF' },
  }),
};
